/**
 * CONFIG SERVICE — API calls for the configurable module platform
 */

const API_URL = process.env.REACT_APP_API_URL || '';

function getAuthToken() {
  try {
    // Login stores to sessionStorage or cookie (helper.setItem); never localStorage.
    const raw = sessionStorage.getItem('logedInUser') || '';
    if (raw) {
      const user = JSON.parse(raw);
      if (user?.token) return user.token;
    }
    const cookie = document.cookie.split(';').find((c) => c.trim().startsWith('logedInUser='));
    if (cookie) {
      const val = decodeURIComponent(cookie.split('=').slice(1).join('='));
      const user = JSON.parse(val);
      if (user?.token) return user.token;
    }
    return '';
  } catch { return ''; }
}

async function apiFetch(path, options = {}) {
  const url = `${API_URL}${path}`;
  const token = getAuthToken();
  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    ...(options.headers || {}),
  };
  const res = await fetch(url, { ...options, headers });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
  return json;
}

const configService = {
  // ── App Settings ─────────────────────────────────────────────────────────
  getSettings: (group) =>
    apiFetch(`/api/config/settings${group ? `?group=${group}` : ''}`),

  saveSettings: (settings) =>
    apiFetch('/api/config/settings', { method: 'PUT', body: JSON.stringify({ settings }) }),

  // ── Favorite modules (per-user, synced across devices) ─────────────────────
  getFavorites: () =>
    apiFetch('/api/me/favorites'),

  saveFavorites: (favorites) =>
    apiFetch('/api/me/favorites', { method: 'PUT', body: JSON.stringify({ favorites }) }),

  // ── Modules ──────────────────────────────────────────────────────────────
  getModules: () =>
    apiFetch('/api/config/modules'),

  getModule: (id) =>
    apiFetch(`/api/config/modules/${id}`),

  getModuleByKey: (key) =>
    apiFetch(`/api/config/modules/by-key/${key}`),

  createModule: (data) =>
    apiFetch('/api/config/modules', { method: 'POST', body: JSON.stringify(data) }),

  updateModule: (id, data) =>
    apiFetch(`/api/config/modules/${id}`, { method: 'PUT', body: JSON.stringify(data) }),

  deleteModule: (id) =>
    apiFetch(`/api/config/modules/${id}`, { method: 'DELETE' }),

  reorderModules: (order) =>
    apiFetch('/api/config/modules-reorder', { method: 'PUT', body: JSON.stringify({ order }) }),

  // ── Sections ─────────────────────────────────────────────────────────────
  getSections: (moduleId) =>
    apiFetch(`/api/config/modules/${moduleId}/sections`),

  createSection: (moduleId, data) =>
    apiFetch(`/api/config/modules/${moduleId}/sections`, { method: 'POST', body: JSON.stringify(data) }),

  updateSection: (id, data) =>
    apiFetch(`/api/config/sections/${id}`, { method: 'PUT', body: JSON.stringify(data) }),

  deleteSection: (id) =>
    apiFetch(`/api/config/sections/${id}`, { method: 'DELETE' }),

  reorderSections: (order) =>
    apiFetch('/api/config/sections-reorder', { method: 'PUT', body: JSON.stringify({ order }) }),

  // ── Fields ───────────────────────────────────────────────────────────────
  getFields: (moduleId) =>
    apiFetch(`/api/config/modules/${moduleId}/fields`),

  createField: (moduleId, data) =>
    apiFetch(`/api/config/modules/${moduleId}/fields`, { method: 'POST', body: JSON.stringify(data) }),

  updateField: (id, data) =>
    apiFetch(`/api/config/fields/${id}`, { method: 'PUT', body: JSON.stringify(data) }),

  deleteField: (id) =>
    apiFetch(`/api/config/fields/${id}`, { method: 'DELETE' }),

  reorderFields: (order) =>
    apiFetch('/api/config/fields-reorder', { method: 'PUT', body: JSON.stringify({ order }) }),

  bulkUpdateFields: (fields) =>
    apiFetch('/api/config/fields-bulk', { method: 'PUT', body: JSON.stringify({ fields }) }),

  updateFieldOptions: (fieldId, options) =>
    apiFetch(`/api/config/fields/${fieldId}/options`, { method: 'PUT', body: JSON.stringify({ options }) }),

  // ── List Config ──────────────────────────────────────────────────────────
  getListConfig: (moduleId) =>
    apiFetch(`/api/config/modules/${moduleId}/list-config`),

  saveListConfig: (moduleId, columns) =>
    apiFetch(`/api/config/modules/${moduleId}/list-config`, { method: 'PUT', body: JSON.stringify({ columns }) }),

  // ── Form Config ──────────────────────────────────────────────────────────
  getFormConfig: (moduleId) =>
    apiFetch(`/api/config/modules/${moduleId}/form-config`),

  saveFormConfig: (moduleId, formType, data) =>
    apiFetch(`/api/config/modules/${moduleId}/form-config/${formType}`, { method: 'PUT', body: JSON.stringify(data) }),

  // ── Dynamic Records ──────────────────────────────────────────────────────
  getDynamicRecords: (moduleKey, params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return apiFetch(`/api/dynamic/${moduleKey}${qs ? '?' + qs : ''}`);
  },

  getDynamicRecord: (moduleKey, id) =>
    apiFetch(`/api/dynamic/${moduleKey}/${id}`),

  createDynamicRecord: (moduleKey, data) =>
    apiFetch(`/api/dynamic/${moduleKey}`, { method: 'POST', body: JSON.stringify(data) }),

  updateDynamicRecord: (moduleKey, id, data) =>
    apiFetch(`/api/dynamic/${moduleKey}/${id}`, { method: 'PUT', body: JSON.stringify(data) }),

  deleteDynamicRecord: (moduleKey, id) =>
    apiFetch(`/api/dynamic/${moduleKey}/${id}`, { method: 'DELETE' }),

  // Custom field values for system modules
  getCustomFieldValues: (moduleKey, recordId) =>
    apiFetch(`/api/dynamic/${moduleKey}/${recordId}/fields`),

  // All custom field values for a module (for list views)
  getAllCustomFieldValues: (moduleKey) =>
    apiFetch(`/api/dynamic/${moduleKey}/custom-fields`),

  saveCustomFieldValues: (moduleKey, recordId, fields) =>
    apiFetch(`/api/dynamic/${moduleKey}/${recordId}/fields`, { method: 'PUT', body: JSON.stringify({ fields }) }),

  // ── Calculations ─────────────────────────────────────────────────────────
  calculate: (calcType, values) =>
    apiFetch('/api/dynamic/calculate', { method: 'POST', body: JSON.stringify({ calcType, values }) }),
};

export default configService;
